import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wrench, MapPin, Play, Square, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const EmployeeOffTicket = ({ auth }) => {
  const [categories, setCategories] = useState([]);
  const [activeEntry, setActiveEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [gpsStatus, setGpsStatus] = useState(null);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [catRes, activeRes, histRes, sumRes] = await Promise.all([
        axios.get(`${API}/offticket/categories`),
        axios.get(`${API}/offticket/active/${auth.employee.id}`),
        axios.get(`${API}/offticket/history/${auth.employee.id}?limit=20`),
        axios.get(`${API}/offticket/summary/${auth.employee.id}`),
      ]);
      setCategories(catRes.data);
      setActiveEntry(activeRes.data);
      setHistory(histRes.data.filter(e => e.end_time));
      setSummary(sumRes.data);
    } catch (err) { console.error(err); }
  }, [auth.employee.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Elapsed timer for active entry
  useEffect(() => {
    if (!activeEntry) { setElapsed(0); return; }
    const start = new Date(activeEntry.start_time).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeEntry]);

  const getLocation = async () => {
    try {
      setGpsStatus('loading');
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 });
      });
      setGpsStatus('ok');
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    } catch {
      setGpsStatus('error');
      return null;
    }
  };

  const handleStart = async () => {
    if (!selectedCategory) {
      toast.error('Choisissez une catégorie');
      return;
    }
    setLoading(true);
    const loc = await getLocation();
    try {
      await axios.post(`${API}/offticket/start`, {
        employee_id: auth.employee.id,
        category: selectedCategory,
        comment,
        latitude: loc?.latitude,
        longitude: loc?.longitude,
      });
      toast.success('Timbrage hors fiche démarré');
      setSelectedCategory('');
      setComment('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!activeEntry) return;
    setLoading(true);
    const loc = await getLocation();
    try {
      const res = await axios.post(`${API}/offticket/stop`, {
        employee_id: auth.employee.id,
        entry_id: activeEntry.id,
        latitude: loc?.latitude,
        longitude: loc?.longitude,
      });
      toast.success(`Terminé - ${res.data.duration_minutes} min`);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const formatElapsed = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getCategoryLabel = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.label : id;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <div className="sticky top-0 z-10 border-b border-white/10 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/employee/dashboard')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Wrench className="w-5 h-5 text-orange-400" />
        <h1 className="text-white font-semibold">Timbrage Hors Fiche</h1>
      </div>

      <div className="flex-1 p-4 max-w-md mx-auto w-full space-y-5">

        {/* Active entry */}
        {activeEntry ? (
          <Card className="p-6 rounded-2xl bg-orange-500/10 border-orange-500/30 space-y-4" data-testid="active-offticket">
            <div className="text-center space-y-2">
              <p className="text-xs uppercase tracking-wider text-orange-400/70">En cours</p>
              <p className="text-sm font-semibold text-orange-300">{getCategoryLabel(activeEntry.category)}</p>
              {activeEntry.comment && <p className="text-xs text-slate-400">{activeEntry.comment}</p>}
              <div className="text-4xl font-mono font-bold text-orange-400 tabular-nums">
                {formatElapsed(elapsed)}
              </div>
              {activeEntry.location_start && (
                <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />
                  <span>Position enregistrée</span>
                </div>
              )}
            </div>
            <Button
              onClick={handleStop}
              disabled={loading}
              className="w-full h-14 rounded-2xl text-base font-bold bg-red-600 hover:bg-red-700 text-white"
              data-testid="stop-offticket-btn"
            >
              <Square className="w-5 h-5 mr-2" />
              {loading ? 'Arrêt...' : 'ARRÊTER'}
            </Button>
          </Card>
        ) : (
          <Card className="p-5 rounded-2xl bg-slate-800/80 border-slate-700 space-y-4" data-testid="start-offticket-form">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1.5 block">Catégorie</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white" data-testid="offticket-category">
                    <SelectValue placeholder="Choisir l'activité..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1.5 block">Commentaire (optionnel)</label>
                <Input
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Ex: Nettoyage pont élévateur..."
                  className="h-12 rounded-xl bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="offticket-comment"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <MapPin className="w-3.5 h-3.5" />
              <span>La position GPS sera enregistrée au démarrage</span>
              {gpsStatus === 'ok' && <span className="text-emerald-400 ml-auto">GPS OK</span>}
              {gpsStatus === 'error' && <span className="text-red-400 ml-auto">GPS indisponible</span>}
            </div>

            <Button
              onClick={handleStart}
              disabled={!selectedCategory || loading}
              className="w-full h-14 rounded-2xl text-base font-bold bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="start-offticket-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              {loading ? 'Démarrage...' : 'DÉMARRER'}
            </Button>
          </Card>
        )}

        {/* Summary */}
        {summary && summary.total_entries > 0 && (
          <Card className="p-4 rounded-2xl bg-slate-800/60 border-slate-700" data-testid="offticket-summary">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3">30 derniers jours</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xl font-mono font-bold text-white">{summary.total_entries}</p>
                <p className="text-[10px] text-slate-400">Activités</p>
              </div>
              <div>
                <p className="text-xl font-mono font-bold text-orange-400">{summary.total_hours}h</p>
                <p className="text-[10px] text-slate-400">Total</p>
              </div>
              <div>
                <p className="text-xl font-mono font-bold text-slate-300">
                  {summary.total_entries > 0 ? Math.round(summary.total_minutes / summary.total_entries) : 0}
                </p>
                <p className="text-[10px] text-slate-400">Moy. min</p>
              </div>
            </div>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 px-1">Historique récent</h3>
            {history.slice(0, 10).map(entry => (
              <Card key={entry.id} className="p-3 rounded-xl bg-slate-800/50 border-slate-700/50" data-testid={`offticket-history-${entry.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
                    <Wrench className="w-4 h-4 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{getCategoryLabel(entry.category)}</p>
                    <p className="text-xs text-slate-400">
                      {entry.date && format(new Date(entry.date), 'd MMM', { locale: fr })} - {entry.duration_minutes ? `${Math.round(entry.duration_minutes)} min` : '--'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Clock className="w-3 h-3" />
                    {entry.start_time && new Date(entry.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeOffTicket;
